import React, { useEffect, useRef, useState } from 'react';
import { FileText, FileSpreadsheet, Presentation, FileImage, FileArchive, FileCode, File as FileIcon, Loader2, AlertCircle } from 'lucide-react';
import { roomFileUrl, fileKind, splitName, formatBytes } from '../../utils/roomFileUrls.js';

/**
 * FILE CARD (CONTEXT-DOCS.md §P1) — a shared document under its message, LOW PROFILE: a preview (or a type icon),
 * the title, the type + size. Click → the Doc pane. PREVIEWS ARE BROWSER-ONLY (zero server cost — owner decision):
 * an image shows itself; a PDF's first page is rendered by pdf.js in the browser; both ONLY once the card scrolls into
 * view, so a long room never downloads files nobody looks at. Office / text / archives show their type icon.
 */
const ICONS = { doc: FileText, text: FileText, sheet: FileSpreadsheet, slides: Presentation, image: FileImage,
  archive: FileArchive, code: FileCode, pdf: FileText };
const TONES = { pdf: 'text-rose-600 bg-rose-50', doc: 'text-sky-700 bg-sky-50', sheet: 'text-emerald-700 bg-emerald-50',
  slides: 'text-orange-600 bg-orange-50', image: 'text-violet-600 bg-violet-50', archive: 'text-amber-700 bg-amber-50',
  text: 'text-gray-600 bg-gray-100', code: 'text-gray-600 bg-gray-100' };

/** True once the element has been on screen (then stays true). */
export function useSeen(ref) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); } },
      { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, seen]);
  return seen;
}

const _pdfThumbs = new Map();   // url-key → dataURL (one render per file per session)

/** Page 1 of a PDF → a data URL, in the browser (pdf.js, lazy-loaded; the worker runs off the main thread). */
async function renderPdfFirstPage(url, width = 288) {
  const pdfjs = await import('pdfjs-dist');
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
  }
  const doc = await pdfjs.getDocument({ url, disableAutoFetch: true, disableStream: false }).promise;
  try {
    const page = await doc.getPage(1);
    const vp1 = page.getViewport({ scale: 1 });
    const vp = page.getViewport({ scale: width / vp1.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(vp.width);
    canvas.height = Math.floor(vp.height);
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    return { src: canvas.toDataURL('image/png'), pages: doc.numPages };
  } finally {
    doc.destroy();
  }
}

export default function FileCard({ networkId, poolId, message, att, onOpen, active = false, previews = true }) {
  const ref = useRef(null);
  const seen = useSeen(ref);
  const kind = fileKind(att.name, att.type);
  const { title, ext } = splitName(att.name);
  const [thumb, setThumb] = useState('');
  const [pages, setPages] = useState(0);
  const Icon = ICONS[kind] || FileIcon;
  const wantsPreview = previews && (kind === 'image' || kind === 'pdf');
  // a different file in this card → never keep the previous file's preview
  useEffect(() => { setThumb(''); setPages(0); }, [message.id, att.index, att.name]);

  useEffect(() => {
    if (!wantsPreview || !seen || !networkId || thumb) return undefined;
    let gone = false;
    const key = `${networkId}|${message.id}|${att.index}`;
    if (_pdfThumbs.has(key)) { const t = _pdfThumbs.get(key); setThumb(t.src); setPages(t.pages); return undefined; }
    roomFileUrl(networkId, poolId, message, att.index)
      .then(async (r) => {
        if (gone || !r || !r.url) return;
        if (kind === 'image') { setThumb(r.url); return; }
        const t = await renderPdfFirstPage(r.url);
        _pdfThumbs.set(key, t);
        if (!gone) { setThumb(t.src); setPages(t.pages); }
      })
      .catch(() => { /* no preview → the type icon stays */ });
    return () => { gone = true; };
  }, [wantsPreview, seen, networkId, poolId, message, att.index, kind, thumb]);

  const ex = att.extract || {};
  const nPages = pages || ex.pages || 0;
  const unit = kind === 'slides' ? 'slide' : kind === 'sheet' ? 'sheet' : 'page';
  const meta = [ext, nPages ? `${nPages} ${unit}${nPages === 1 ? '' : 's'}` : '', formatBytes(att.size)].filter(Boolean).join(' · ');
  return (
    <button type="button" ref={ref} data-testid={`file-card-${message.id}-${att.index}`} data-kind={kind}
      aria-label={`Open ${att.name}`} title={att.name} onClick={() => onOpen(message, att)}
      className={`group/fc flex w-72 max-w-full flex-col overflow-hidden rounded-lg border text-left transition-colors ${
        active ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}>
      {thumb ? (
        <span className="block h-32 w-full overflow-hidden border-b border-gray-100 bg-gray-50">
          <img src={thumb} alt="" data-testid={`file-thumb-${message.id}-${att.index}`}
            className="h-full w-full object-cover object-top" onError={() => setThumb('')} />
        </span>
      ) : null}
      <span className="flex items-center gap-2 px-2.5 py-2">
        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${TONES[kind] || TONES.code}`} aria-hidden>
          <Icon className="h-[1.125rem] w-[1.125rem]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.8125rem] font-semibold text-gray-900 group-hover/fc:text-indigo-700">{title}</span>
          <span className="block truncate text-[0.6875rem] text-gray-500">{meta}</span>
        </span>
        {/* EXTRACTION (CONTEXT-DOCS.md §P2): only while reading / when it couldn't be read — ready is the quiet default */}
        {ex.status === 'reading' ? (
          <span data-testid={`file-status-${message.id}-${att.index}`} title="Reading it into the context"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[0.625rem] font-medium text-indigo-700">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />Reading…
          </span>
        ) : ex.status === 'failed' ? (
          <span data-testid={`file-status-${message.id}-${att.index}`} title={ex.error || "Couldn't read it"}
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[0.625rem] font-medium text-rose-700">
            <AlertCircle className="h-3 w-3" aria-hidden />Couldn&apos;t read
          </span>
        ) : null}
      </span>
    </button>
  );
}
